import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { JourneyRail } from "../components/onboarding/JourneyRail";
import { IntroStage } from "../components/onboarding/IntroStage";
import { EmailStage } from "../components/onboarding/EmailStage";
import { OrgDetailsStage } from "../components/onboarding/OrgDetailsStage";
import { ReportingGoalsStage } from "../components/onboarding/ReportingGoalsStage";
import { SummaryStage } from "../components/onboarding/SummaryStage";
import { AdviserPanel } from "../components/onboarding/AdviserPanel";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import Footer from "../components/Footer";

// Phase 1 — capture only. No account or tenant is created here; the journey ends at a
// summary screen and a welcome email carries the user into Phase-2 provisioning.
export default function SelfServiceOnboarding() {
  const [stageIndex, setStageIndex] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState({});
  const [submitResult, setSubmitResult] = useState(null);
  const [adviserOpen, setAdviserOpen] = useState(false);
  const [resuming, setResuming] = useState(true);

  useEffect(() => {
    document.documentElement.classList.add("dark");
    const saved = localStorage.getItem("lestari_onb");
    if (!saved) { setResuming(false); return; }
    (async () => {
      try {
        const { data: s } = await api.get(`/onboarding/session/${saved}`);
        if (!s || ["captured", "account_created", "activated"].includes(s.stage)) {
          localStorage.removeItem("lestari_onb");
          setResuming(false);
          return;
        }
        setSessionId(saved);
        const emailVerified = !!(s.email_verification && s.email_verification.verified);
        setSession({ profile: s.profile, organisation: s.organisation,
          verifiedEmail: (s.representative || {}).email });
        if (s.organisation && emailVerified) setStageIndex(3);
        else if (emailVerified) setStageIndex(2);
        else setStageIndex(1);
      } catch {
        localStorage.removeItem("lestari_onb");
      } finally {
        setResuming(false);
      }
    })();
  }, []);

  const startSession = async () => {
    try {
      const { data } = await api.post("/onboarding/start");
      setSessionId(data.session_id);
      localStorage.setItem("lestari_onb", data.session_id);
      setStageIndex(1);
    } catch {
      toast.error("Could not start onboarding. Please retry.");
    }
  };

  useEffect(() => { setMaxReached((m) => Math.max(m, stageIndex)); }, [stageIndex]);

  const goToStage = (i) => {
    if (i < 1 || i > maxReached || i >= 4) return; // summary isn't re-navigable
    setStageIndex(i);
  };

  if (resuming) {
    return <div className="h-screen w-screen flex items-center justify-center bg-background text-muted-foreground font-mono text-sm dark">Loading…</div>;
  }

  return (
    <div className="min-h-screen flex bg-background text-foreground dark grid-bg" data-testid="self-service-onboarding">
      <JourneyRail currentIndex={stageIndex} maxReached={maxReached} onStageClick={goToStage} />

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 py-10 lg:py-16">
          {stageIndex === 0 && <IntroStage onStart={startSession} />}
          {stageIndex === 1 && (
            <EmailStage
              sessionId={sessionId}
              onBack={() => setStageIndex(0)}
              onVerified={({ email }) => { setSession((s) => ({ ...s, verifiedEmail: email })); setStageIndex(2); }}
            />
          )}
          {stageIndex === 2 && (
            <OrgDetailsStage
              sessionId={sessionId}
              verifiedEmail={session.verifiedEmail}
              onBack={() => setStageIndex(1)}
              onSaved={(data) => { setSession((s) => ({ ...s, organisation: data.organisation, profile: data.profile })); setStageIndex(3); }}
            />
          )}
          {stageIndex === 3 && (
            <ReportingGoalsStage
              sessionId={sessionId}
              profile={session.profile}
              onBack={() => setStageIndex(2)}
              onDone={(result) => { setSubmitResult(result); localStorage.removeItem("lestari_onb"); setStageIndex(4); }}
            />
          )}
          {stageIndex === 4 && <SummaryStage result={submitResult} />}
        </div>
        <Footer />
      </div>

      {stageIndex > 0 && stageIndex < 4 && (
        <Button variant="primary" className="fixed bottom-6 right-6 shadow-lg gap-2 z-40" onClick={() => setAdviserOpen(true)} data-testid="open-adviser-btn">
          <Sparkles className="h-4 w-4" /> Ask the adviser
        </Button>
      )}
      <Sheet open={adviserOpen} onOpenChange={setAdviserOpen}>
        <SheetContent side="right" className="p-0">
          <SheetHeader>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-accent flex items-center justify-center rounded-sm">
                <Sparkles className="h-4 w-4 text-black" />
              </div>
              <div>
                <SheetTitle>Onboarding adviser</SheetTitle>
                <SheetDescription>Plain-language ESG guidance</SheetDescription>
              </div>
            </div>
          </SheetHeader>
          <AdviserPanel sessionId={sessionId} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
